import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import type {TypographyProps} from '@mui/material/Typography';
import type {BoxProps} from '@mui/material/Box';
import Eyebrow from '@/components/Common/Eyebrow';
import {contentMaxWidth} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

interface SectionHeaderProps {
    /**
     * Kicker above the heading. A string is wrapped in the canonical `<Eyebrow>`
     * atom; a node is rendered as-is for the rare section with a bespoke eyebrow.
     */
    eyebrow?: React.ReactNode;
    /** Heading text or already-parsed rich-text nodes. */
    heading: React.ReactNode;
    /** Optional supporting copy below the heading. */
    subheading?: React.ReactNode;
    /** Wrapper text alignment. Defaults to centered. */
    align?: BoxProps['textAlign'];
    /** Extra sx merged onto the heading (e.g. a color override). */
    headingSx?: SxProps<Theme>;
    /** Subheading typography variant. Defaults to `subtitle1`. */
    subheadingVariant?: TypographyProps['variant'];
    /** Extra class names appended after `font-regular-text` on the subheading. */
    subheadingClassName?: string;
    /** Extra sx merged onto the subheading. */
    subheadingSx?: SxProps<Theme>;
    /** Extra sx merged onto the outer wrapper Box (overrides the default maxWidth/centering/mb). */
    wrapperSx?: SxProps<Theme>;
}

/**
 * SectionHeader (Molecule) — the centered eyebrow / heading / subheading block
 * repeated across section components. Encapsulates the shared wrapper (max-width,
 * centering, spacing) and the canonical heading (`h1` styled, rendered as `h2`)
 * and subheading typography, while exposing the small per-section variations
 * (alignment, spacing, color, extra classes) as optional props.
 */
const SectionHeader = memo(function SectionHeader({
    eyebrow,
    heading,
    subheading,
    align = 'center',
    headingSx,
    subheadingVariant = 'subtitle1',
    subheadingClassName,
    subheadingSx,
    wrapperSx
}: SectionHeaderProps) {
    const subheadingClasses = ['font-regular-text', subheadingClassName].filter(Boolean).join(' ');

    return (
        <Box
            textAlign={align}
            sx={
                [
                    {
                        maxWidth: contentMaxWidth.standard,
                        margin: '0 auto',
                        mb: 4
                    },
                    wrapperSx
                ] as SxProps<Theme>
            }
        >
            {typeof eyebrow === 'string' ? <Eyebrow text={eyebrow} /> : eyebrow}
            <Typography
                variant="h1"
                component="h2"
                mb={1.5}
                className="font-headers"
                sx={[sectionHeadingSx, ...(Array.isArray(headingSx) ? headingSx : [headingSx])]}
            >
                {heading}
            </Typography>
            {subheading !== undefined && subheading !== null && subheading !== '' && (
                <Typography variant={subheadingVariant} component="p" className={subheadingClasses} sx={subheadingSx}>
                    {subheading}
                </Typography>
            )}
        </Box>
    );
});

export default SectionHeader;
